import { Button } from "@bcc/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@bcc/components/ui/dropdown-menu";
import type { Column, Table } from "@tanstack/react-table";
import { Columns3, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { DataTableRow } from "./data-table.types";
import {
  setColumnVisibilityContentTestId,
  setColumnVisibilityItemTestId,
  setColumnVisibilityResetTestId,
  setColumnVisibilityTriggerTestId,
} from "./data-table.utils";

export interface DataTableColumnVisibilityProps<T extends object> {
  table: Table<DataTableRow<T>>;
  /** Restore the table's default visibility map and drop any persisted override. */
  onReset: () => void;
  /**
   * Resolves a human-readable label for a column id. Supplied by the consumer so
   * this shared component never has to know about any one table's i18n keys.
   */
  getColumnLabel?: (columnId: string) => string;
  triggerLabel?: string;
  menuLabel?: string;
  resetLabel?: string;
}

/**
 * Whether any column on the table can be hidden. Shared with `DataTable`, which uses it to decide
 * whether to mount this menu at all — one definition so the two checks can't drift apart.
 */
export const hasHideableColumns = <T extends object>(table: Table<DataTableRow<T>>): boolean =>
  table.getAllLeafColumns().some((column) => column.getCanHide());

/**
 * Falls back through consumer-supplied label → plain-string header → column id.
 * Non-string headers (React components) can't be read as text here, which is why
 * `getColumnLabel` exists.
 */
const resolveColumnLabel = <T extends object>(column: Column<DataTableRow<T>, unknown>, getColumnLabel?: (id: string) => string) => {
  const consumerLabel = getColumnLabel?.(column.id);
  if (consumerLabel) return consumerLabel;
  const header = column.columnDef.header;
  return typeof header === "string" && header.length > 0 ? header : column.id;
};

/**
 * DataTableColumnVisibility
 *
 * Dropdown of checkbox items — one per hideable column — plus a reset item.
 * Columns declared with `enableHiding: false` are excluded by `getCanHide()`
 * and can never be toggled off.
 */
export function DataTableColumnVisibility<T extends object>({
  table,
  onReset,
  getColumnLabel,
  triggerLabel: triggerLabelProp,
  menuLabel: menuLabelProp,
  resetLabel: resetLabelProp,
}: Readonly<DataTableColumnVisibilityProps<T>>) {
  const { t } = useTranslation();
  const triggerLabel = triggerLabelProp ?? t("dataTable.columnVisibility.trigger");
  const menuLabel = menuLabelProp ?? t("dataTable.columnVisibility.label");
  const resetLabel = resetLabelProp ?? t("dataTable.columnVisibility.reset");

  const hideableColumns = table.getAllLeafColumns().filter((column) => column.getCanHide());

  // Nothing to toggle — don't render a dead trigger. `DataTable` gates on the same condition via
  // `hasHideableColumns`, but this component is exported standalone so it guards itself too.
  if (hideableColumns.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild testId={setColumnVisibilityTriggerTestId()}>
        <Button variant="outline" size="sm" className="h-8">
          <Columns3 className="size-4 mr-1" />
          {triggerLabel}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" testId={setColumnVisibilityContentTestId()}>
        <DropdownMenuLabel>{menuLabel}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {hideableColumns.map((column) => (
          <DropdownMenuCheckboxItem
            key={column.id}
            data-testid={setColumnVisibilityItemTestId(column.id)}
            checked={column.getIsVisible()}
            onCheckedChange={(value) => column.toggleVisibility(!!value)}
            // Keep the menu open so several columns can be toggled in one go.
            onSelect={(event) => event.preventDefault()}
          >
            {resolveColumnLabel(column, getColumnLabel)}
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem testId={setColumnVisibilityResetTestId()} onSelect={onReset}>
          <RotateCcw className="size-4 mr-1" />
          {resetLabel}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
